import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { NS } from '../../U3130';
import type { U2000TimesheetWeekDay } from '../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import { U3130_HONORARIUM_NO_PROJECT_KEY, type U3130HonorariumProjectGroup } from '../../tools/u3130GroupHonorariumForDisplay';
import {
  U3130_HONORARIUM_CELL_X,
  U3130_HONORARIUM_CLICKABLE_PARENT_ROW_CLASS,
  U3130_HONORARIUM_COLLAPSED_PARENT_ROW_CLASS,
  U3130_HONORARIUM_CUSTOMER_COL_CLASS,
  U3130_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS,
  U3130_HONORARIUM_EXPANDED_PARENT_ROW_CLASS,
  U3130_HONORARIUM_HOUR_TD_CLASS,
  U3130_HONORARIUM_NATURES_COL_CLASS,
  U3130_HONORARIUM_PROJECT_COL_CLASS,
  U3130_HONORARIUM_SUBPROJECT_COL_CLASS,
  U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS,
} from '../../tools/u3130HonorariumGridCompactStyles';

export interface U3130HonorariumProjectHeaderRowProps {
  group: U3130HonorariumProjectGroup;
  expanded: boolean;
  onToggle: () => void;
  visibleDays: readonly U2000TimesheetWeekDay[];
  showSubProjectColumn: boolean;
  isFirstProject: boolean;
  formatHours: (value: number) => string;
}

/** Ligne client d’un dossier (rendu U3129) : clic sur la ligne ou le chevron pour afficher / masquer les tâches. */
const U3130HonorariumProjectHeaderRow: React.FC<U3130HonorariumProjectHeaderRowProps> = ({
  group,
  expanded,
  onToggle,
  visibleDays,
  showSubProjectColumn,
  isFirstProject,
  formatHours,
}) => {
  const { t } = useTranslation();
  const noProject = group.projectKey === U3130_HONORARIUM_NO_PROJECT_KEY;
  const projectLabel = noProject ? t('lblHonorariumNoProjectGroup', { ns: NS, defaultValue: 'Sans projet' }) : group.projectLabel;
  const toggleLabel = expanded
    ? t('btnCollapseHonorariumProject', { ns: NS, defaultValue: 'Replier le projet' })
    : t('btnExpandHonorariumProject', { ns: NS, defaultValue: 'Développer le projet' });

  return (
    <tr
      className={`${U3130_HONORARIUM_CLICKABLE_PARENT_ROW_CLASS} border-b ${expanded ? `border-blue-100 ${U3130_HONORARIUM_EXPANDED_PARENT_ROW_CLASS}` : `border-gray-200 ${U3130_HONORARIUM_COLLAPSED_PARENT_ROW_CLASS}`} ${isFirstProject ? '' : 'border-t-2 border-t-gray-300'}`}
      onClick={onToggle}
    >
      <td className={`w-10 pl-2 pr-1 py-1.5 align-middle ${expanded ? U3130_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS : ''}`}>
        <button
          type="button"
          className={`inline-flex h-8 w-8 items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${expanded ? 'text-blue-700 hover:bg-blue-100' : 'text-gray-600 hover:bg-gray-200/80'}`}
          aria-expanded={expanded}
          title={toggleLabel}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        >
          {expanded ? <FaChevronDown className="h-3.5 w-3.5" aria-hidden /> : <FaChevronRight className="h-3.5 w-3.5" aria-hidden />}
          <span className="sr-only">{toggleLabel}</span>
        </button>
      </td>
      <td className={`${U3130_HONORARIUM_CELL_X} py-1.5 align-middle text-left font-medium text-gray-800 ${U3130_HONORARIUM_PROJECT_COL_CLASS}`}>
        <div className={`${U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} truncate font-semibold`} title={projectLabel}>
          {projectLabel}
        </div>
      </td>
      <td className={`${U3130_HONORARIUM_CELL_X} py-1.5 align-middle text-left ${U3130_HONORARIUM_CUSTOMER_COL_CLASS}`}>
        <div className={`${U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} truncate`} title={noProject ? undefined : group.clientLabel || undefined}>
          {noProject ? '—' : group.clientLabel}
        </div>
      </td>
      <td className={`${U3130_HONORARIUM_CELL_X} py-1.5 align-middle text-left ${U3130_HONORARIUM_NATURES_COL_CLASS}`}>
        <div className={`${U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} truncate tabular-nums`} title={group.workNatureDisplay || undefined}>
          {group.workNatureDisplay}
        </div>
      </td>
      {showSubProjectColumn ? (
        <td className={`${U3130_HONORARIUM_CELL_X} py-1.5 align-middle text-left ${U3130_HONORARIUM_SUBPROJECT_COL_CLASS}`}>
          <div className={U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>—</div>
        </td>
      ) : null}
      {visibleDays.map((d) => (
        <td key={d} className={`${U3130_HONORARIUM_HOUR_TD_CLASS} align-middle text-right`}>
          <div className={`${U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums font-medium text-gray-800`}>
            {formatHours(group.totals.daySums[d] ?? 0)}
          </div>
        </td>
      ))}
      <td className={`${U3130_HONORARIUM_HOUR_TD_CLASS} align-middle text-right`}>
        <div className={`${U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums font-semibold text-gray-900`}>
          {formatHours(group.totals.grandTotal)}
        </div>
      </td>
    </tr>
  );
};

export default U3130HonorariumProjectHeaderRow;
